import { RefObject, useEffect } from 'react';

type CopyRule = {
  /** SDK elements whose own text nodes are rewritten */
  selector: string;
  /** Returns the new text, or the same text to leave it */
  rewrite: (text: string) => string;
};

// Listed one by one, as other labels keep proper nouns and acronyms ("City (e.g. New York)", "CVV")
const SENTENCE_CASE_COPY: Record<string, string> = {
  'Routing Number': 'Routing number',
  'Account Number': 'Account number',
  'Card Number': 'Card number',
  'Expiration Date': 'Expiration date',
  'Set Up my Protection': 'Set up my protection',
};

const COPY_RULES: CopyRule[] = [
  {
    selector: 'label, button',
    rewrite: (text) => SENTENCE_CASE_COPY[text] ?? text,
  },
  // The promotion footnote comes from the API with a leading asterisk, which marks nothing here
  {
    selector: "[data-gc-dg-element$='-monthly-promotion-text']",
    rewrite: (text) => text.replace(/^\*\s*/, ''),
  },
  {
    selector: '.gc-dg-btn-edit',
    rewrite: (text) => (text === 'Edit' ? 'Change' : text),
  },
  {
    selector: '.gc-dg-btn-save',
    rewrite: (text) => (text === 'Save' ? 'Update billing address' : text),
  },
];

const rewriteTextNode = (textNode: Text, rewrite: CopyRule['rewrite']) => {
  const text = textNode.data.trim();
  if (!text) return;
  const replacement = rewrite(text);
  if (replacement !== text) textNode.data = textNode.data.replace(text, replacement);
};

const ACH_OFFER_SELECTOR =
  "[data-gc-dg-element='gc-dg-ach-payment-discount-banner'] .gc-dg-discount-box strong";

/**
 * @description Rewrites SDK copy in place (text nodes only), so the SDK keeps its elements and
 * handlers.
 */
export const useSdkCopy = (panelRef: RefObject<HTMLElement | null>, achOffer?: string) => {
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const rules: CopyRule[] = achOffer
      ? [...COPY_RULES, { selector: ACH_OFFER_SELECTOR, rewrite: () => achOffer }]
      : COPY_RULES;

    const rewriteAll = () => {
      rules.forEach(({ selector, rewrite }) => {
        panel.querySelectorAll(selector).forEach((element) => {
          element.childNodes.forEach((node) => {
            if (node instanceof Text) rewriteTextNode(node, rewrite);
          });
        });
      });
    };

    // characterData: the SDK may update a text node in place. A rewritten node no longer matches.
    const observer = new MutationObserver(rewriteAll);
    observer.observe(panel, { childList: true, subtree: true, characterData: true });
    rewriteAll();

    return () => observer.disconnect();
  }, [panelRef, achOffer]);
};
